import { useCallback, useEffect, useRef, useState } from 'react';

/** Keep at least this much of a gap between the bar and the viewport edges. */
const VIEWPORT_MARGIN = 8;
/** Pointer travel before a press counts as a drag (so taps still read as clicks). */
const DRAG_THRESHOLD = 3;

/** Elements that own the pointer themselves — pressing these must not start a drag. */
const INTERACTIVE_SELECTOR =
    'button, a, input, select, textarea, [role="button"], [role="menuitem"], [role="checkbox"], [contenteditable="true"]';

interface DragBounds {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
}

/**
 * Makes a floating bar (bulk actions, save/discard) draggable so it can be moved
 * out of the way of whatever it covers.
 *
 * Pass whether the bar is currently showing: these bars render `null` rather than
 * unmounting, so the offset has to be cleared explicitly to bring the bar back to
 * its default spot the next time it appears.
 */
const useDraggableFloatingBar = (isVisible: boolean) => {
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const barRef = useRef<HTMLDivElement | null>(null);
    const dragRef = useRef<{
        pointerId: number;
        startX: number;
        startY: number;
        originX: number;
        originY: number;
        bounds: DragBounds;
        moved: boolean;
    } | null>(null);

    const clamp = (value: number, min: number, max: number) =>
        Math.min(Math.max(value, min), Math.max(min, max));

    const onPointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (event.button !== 0 && event.pointerType === 'mouse') return;
            const target = event.target as HTMLElement | null;
            if (target?.closest(INTERACTIVE_SELECTOR)) return;

            const node = barRef.current;
            if (!node) return;

            const rect = node.getBoundingClientRect();
            // Where the bar sits with no offset applied — the basis for clamping.
            const baseLeft = rect.left - offset.x;
            const baseTop = rect.top - offset.y;

            dragRef.current = {
                pointerId: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                originX: offset.x,
                originY: offset.y,
                bounds: {
                    minX: VIEWPORT_MARGIN - baseLeft,
                    maxX: window.innerWidth - VIEWPORT_MARGIN - rect.width - baseLeft,
                    minY: VIEWPORT_MARGIN - baseTop,
                    maxY: window.innerHeight - VIEWPORT_MARGIN - rect.height - baseTop,
                },
                moved: false,
            };

            node.setPointerCapture(event.pointerId);
            setIsDragging(true);
        },
        [offset.x, offset.y],
    );

    const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;

        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        if (!drag.moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        drag.moved = true;

        setOffset({
            x: clamp(drag.originX + dx, drag.bounds.minX, drag.bounds.maxX),
            y: clamp(drag.originY + dy, drag.bounds.minY, drag.bounds.maxY),
        });
    }, []);

    const endDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        dragRef.current = null;
        barRef.current?.releasePointerCapture?.(event.pointerId);
        setIsDragging(false);
    }, []);

    // Back to the default spot whenever the bar goes away, so it never reappears
    // parked wherever it was last dragged.
    useEffect(() => {
        if (!isVisible) {
            dragRef.current = null;
            setIsDragging(false);
            setOffset({ x: 0, y: 0 });
        }
    }, [isVisible]);

    // A resize can leave a moved bar off-screen; pull it back into view.
    useEffect(() => {
        if (offset.x === 0 && offset.y === 0) return;
        const handleResize = () => {
            const node = barRef.current;
            if (!node) return;
            const rect = node.getBoundingClientRect();
            const baseLeft = rect.left - offset.x;
            const baseTop = rect.top - offset.y;
            const next = {
                x: clamp(
                    offset.x,
                    VIEWPORT_MARGIN - baseLeft,
                    window.innerWidth - VIEWPORT_MARGIN - rect.width - baseLeft,
                ),
                y: clamp(
                    offset.y,
                    VIEWPORT_MARGIN - baseTop,
                    window.innerHeight - VIEWPORT_MARGIN - rect.height - baseTop,
                ),
            };
            if (next.x !== offset.x || next.y !== offset.y) setOffset(next);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [offset.x, offset.y]);

    /** Spread onto the pill itself (the element that should move). */
    const dragHandleProps = {
        ref: barRef,
        onPointerDown,
        onPointerMove,
        onPointerUp: endDrag,
        onPointerCancel: endDrag,
        style: {
            transform: `translate(${offset.x}px, ${offset.y}px)`,
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none' as const,
            userSelect: isDragging ? ('none' as const) : undefined,
        },
    };

    return { dragHandleProps, isDragging, hasMoved: offset.x !== 0 || offset.y !== 0 };
};

export default useDraggableFloatingBar;
